const _ = require('lodash');
const DirectRender = require('./directRender');
const DynamicInsert = require('./dynamicInsert');
const ReloadDefaults = require('./reloadDefaults');
const { PLACEMENT_UI_GROUPS: UI_GROUPS } = require('./constants');
const { CURRENCIES } = require('../constants');
const { hideIfNot, hasFloorProp, hideWhenNoEmpty } = require('./bidParamUiUtils');

const FILTERS = {
	LINE_ITEM_TYPES: 'Line item types',
	DESC_STRING: `Line item types/Advertisers/Orders`,
};

const Fields = [
	{
		type: 'Number',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvFloor',
		description: 'Price floor',
		omitWhenDefault: true,
		boostType: 'floor',
	},
	{
		type: 'Boolean',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvFloorOpt',
		description: 'Optimize floor price',
		omitWhenDefault: true,
		optiField: true,
	},
	{
		type: 'Boolean',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvFloorOptOnly',
		description: 'Only set price floor for optimized users',
		omitWhenDefault: true,
		optiField: true,
	},
	{
		type: 'Boolean',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvFloorOptAllowNo',
		description: 'Allow optimizer to disable floor even when non-zero',
		omitWhenDefault: true,
		optiField: true,
	},
	{
		type: 'String',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvFloorCur',
		description: 'Price floor currency',
		defaultValue: null,
		hasOptions: true,
		options: Object.entries(CURRENCIES).map(([code, { name }]) => ({
			name: code,
			label: `${name} (${code})`,
		})),
		omitWhenDefault: true,
	},
	{
		type: 'String',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvFloorType',
		description: 'Set price floors for',

		// Hide setting for placements where adserver can't use floors anyway, except if it's set to 'adsOnly'
		shouldHide: (ctx, { value }) => !hasFloorProp(ctx, 'canSetFloors') && value !== 'adsOnly',
		isRequired: true,
		defaultValue: 'hbOnly',
		omitWhenDefault: true,
		hasOptions: true,
		options: [
			{
				name: 'both',
				label: 'Both Headerbidding and Adserver',
			},
			{
				name: 'hbOnly',
				label: 'Headerbidding only',
			},
			{
				name: 'adsOnly',
				label: 'Adserver only',
			},
		],
	},
	{
		type: 'Number',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvAdsOptiPerc',
		description: 'Adserver optimized %',

		// Hide setting for placements where adserver can't optimize anyway
		shouldHide: (ctx) => !hasFloorProp(ctx, 'canAdsOptimize'),
		omitWhenDefault: true,
		props: {
			between: { low: 0, high: 100 },
			help: 'Adserver optimized users will ignore any price floor set above',
		},
	},
	{
		type: 'Boolean',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvDisableHb',
		description: 'Disable all headerbidding',
		omitWhenDefault: true,
		props: { help: 'Notice: Disabling of headerbidding using this setting only works for web' },
	},
	{
		// Essentially maps to disableCollapse option for appnexus renderer settings
		type: 'Object',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvReloadSettings',
		description: 'Reload settings',
		uiHelp: { query: 'Ad-refresh', question: 'Explain the ad-refresh (reload settings) feature for placements, including the rlvReloadSettings parameters and best practices for ad performance' },
		defaultValue: {},
		fields: [
			{
				type: 'Boolean',
				name: 'enabled',
				description: 'Enable placement reloading',
				defaultValue: false,
			},
			{
				type: 'Number',
				name: 'timeBetween',
				description: 'Reload interval in seconds',
				isRequired: true,
				props: {
					between: { low: 3, high: null },
				},
			},
			{
				type: 'String',
				name: 'strategy',
				description: 'Count reload interval by',
				isRequired: true,
				hasOptions: true,
				options: [
					{
						name: 'load',
						label: 'Time since loaded',
					},
					{
						name: 'viewStart',
						label: 'Time since first time viewable',
					},
					{
						name: 'sticky',
						label: 'Time being viewable',
					},
				],
			},
			{
				type: 'String',
				name: 'hiddenType',
				description: 'Count the page as visible',
				hasOptions: true,
				isRequired: true,
				options: [
					{
						name: 'focus',
						label: 'When document is focused',
					},
					{
						name: 'hidden',
						label: 'When document is not hidden',
					},
					{
						name: 'none',
						label: 'Always (no check, not recommended)',
					},
				],
			},
			{
				type: 'Number',
				name: 'reloadTimes',
				description: 'Maximum number of reloads',
				isRequired: true,
			},
			{
				type: 'Number',
				name: 'minVisibility',
				description: 'Minimum viewability % for reload',
				isRequired: true,
			},
			{
				type: 'Boolean',
				name: 'keepDims',
				description: 'Set CSS min width/height when reloading (prevents jump-effect)',
			},
			{
				type: 'Boolean',
				name: 'divChangeOk',
				description: 'Continue reloading after ad-div replacement',
				props: {
					help: 'If enabled on an SPA-page - make sure relevantDigital.destroySlots() is called between page switches.',
				},
			},
			{
				type: 'Boolean',
				name: 'allowPostMessage',
				description: 'Allow updating settings via postMessage API',
			},
			{
				type: 'Boolean',
				name: 'retryOnEmptyReturn',
				description: 'Refresh if empty ad return',
			},
			{
				type: 'Number',
				name: 'maxEmptyReturnRetries',
				description: 'Maximum retries after empty return',
				isRequired: true,
				shouldHide: hideWhenNoEmpty,
				props: {
					between: { low: 1, high: 5 },
					help: 'Up to 5 extra reloads is allowed',
				},
			},
			{
				type: 'Number',
				name: 'timeBetweenEmptyRetry',
				description: 'Retry delay in seconds after an empty ad return. Multiplied by retry count',
				isRequired: true,
				shouldHide: hideWhenNoEmpty,
				props: {
					between: { low: 0, high: null },
				},
			},
			{
				type: 'Boolean',
				name: 'noSpaceIsEmpty',
				description: 'Consider ads as empty/no-ads when ad-div client area is zero pixels',
				shouldHide: hideWhenNoEmpty,
				props: {
					help: 'Use this when using auto-collapsing default-ads or similar solutions',
				},
			},
			{
				type: 'Number',
				name: 'noSpaceTime',
				description: 'Minimum time for an ad to stay collapsed before considered empty (seconds)',
				shouldHide: (...args) => hideWhenNoEmpty(...args) || hideIfNot('noSpaceIsEmpty')(...args),
				isRequired: true,
				props: {
					between: { low: 1, high: null },
					help: 'Increase this number if valid ads tends to stay collapsed for longer periods and \'Retry delay...\' is short',
				},
			},
			{
				type: 'String',
				name: 'fType',
				description: 'Filter type',
				isRequired: true,
				hasOptions: true,
				options: [
					{
						name: 'incl',
						label: `Include ${FILTERS.DESC_STRING}`,
					},
					{
						name: 'excl',
						label: `Exclude ${FILTERS.DESC_STRING}`,
					},
				],
			},
			{
				type: 'Array',
				description: `Filter ${FILTERS.DESC_STRING} (only supported in GAM)`,
				name: 'filter',
				fields: [
					{
						type: 'String',
						name: 'liType',
						description: `${FILTERS.LINE_ITEM_TYPES} (requires GAM Real time import)`,
						hasOptions: true,
						multiSelect: true,
						options: [
							{
								name: 'hb',
								label: 'Headerbidding',
							},
							// "name" must match enum values in AdserverLineItemType
							...['Sponsorship', 'Standard', 'Network', 'Bulk', 'Price Priority', 'House',
								'Click Tracking', 'Ad Exchange', 'Adsense', 'Bumper', 'Admob', 'Preferred Deal']
								.map((label, idx) => ({ label, name: (idx + 1).toString() })),
						],
					},
					{
						uiGroup: UI_GROUPS.ADV_ORDER,
						type: 'String',
						name: 'type',
						description: 'Object type',
						defaultValue: 'adv',
						isRequired: true,
						hasOptions: true,
						options: [
							{
								name: 'adv',
								label: 'Advertiser ID',
							},
							{
								name: 'order',
								label: 'Order ID',
							},
						],
					},
					{

						uiGroup: UI_GROUPS.ADV_ORDER,
						type: 'String',
						name: 'value',
						description: 'ID (can be left blank if line item type is selected)',
					},
				],
			},
		],
	},
	{
		// Essentially maps to disableCollapse option for appnexus renderer settings
		type: 'Object',
		uiGroup: UI_GROUPS.BUILT_IN,
		name: 'rlvLazy',
		description: 'Lazy load settings',
		uiHelp: { query: 'Lazy-loading', question: 'Explain the lazy-loading feature for ad placements, including the rlvLazy parameters and how it affects ad performance' },
		defaultValue: {},
		fields: [
			{
				type: 'Boolean',
				name: 'enabled',
				description: 'Lazy loading',
				defaultValue: false,
			},
			{
				type: 'Number',
				name: 'pixels',
				description: 'Pixel distance from screen (vertically)',
				defaultValue: 0,
				isRequired: true,
			},
			{
				type: 'Number',
				name: 'mobileScaling',
				description: 'Multiplier applied to pixel distance on mobile devices',
				defaultValue: 1,
				isRequired: true,
				omitWhenDefault: true,
			},
		],
	},
	DirectRender.FieldSettings,
	DynamicInsert.FieldSettings,
];

/** Let save space in .js by not including any default values */
_.find(Fields, { name: 'rlvReloadSettings' }).fields.forEach((fld) => {
	if (fld.name in ReloadDefaults) {
		fld.defaultValue = ReloadDefaults[fld.name];
	}
	fld.omitWhenDefault = true;
});

module.exports = Fields;
